import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import OtpTimer from 'otp-timer';
import axios from 'axios';
import { Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RegisterAll = () => {
  const navigate = useNavigate();
  const fieldRefs = useRef({});

  // Loading States
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');

  // OTP States
  const [counter, setcounter] = useState(10);
  const [counterVisible, setcounterVisible] = useState(false);
  const [sendBtnVisible, setSendBtnVisible] = useState(true);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerifyProgress, setOtpVerifyProgress] = useState(false);
  const [otpSendProgress, setOtpSendProgress] = useState(false);

  // Errors & Messages
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState('');
  const [touched, setTouched] = useState({});

  // Dropdown Data
  const [fetchStates, setFetchStates] = useState([]);
  const [fetchDistricts, setFetchDistricts] = useState([]);
  const [fetchCities, setFetchCities] = useState([]);
  const [fetchBusinessCat, setFetchBusinessCat] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState('');

  // Form Data
  const [data, setData] = useState({
    mobile: '',
    otp: '',
    name: '',
    email: '',
    password: '',
    password_confirmation: '',

    // Business Details
    bisiness_name: '', // note the spelling to match backend
    trade_lic_no: '',
    gst_no: '',
    pan_no: '',
    state: '',
    district: '',
    city: '',
    pin: '',
    street: '',
    land_mark: '',
    business_mobile: '',
    alt_mobile: '',
    address: '',
    business_category: '',
    name_of_ac_owner: '',
    ac_no: '',
    bank_name: '',
    ifsc_code: '',
    bank_branch: '',
    business_email: '',
    business_description: '',
  });

  // Document Files
  const [file, setFile] = useState({
    trade_lic_image: null,
    gst_image: null,
    pan_image: null
  });

  const [filePreview, setFilePreview] = useState({
    trade_lic_image: '',
    gst_image: '',
    pan_image: ''
  });

  const [disableSubmit, setDisableSubmit] = useState(true);

  // Load masters on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchState();
    fetchBusinessCategory();
  }, []);

  // --- OTP Verification Logic ---
  const handleMobileChange = (value) => {
    // Only allow numbers
    const cleanValue = value.replace(/[^0-9]/g, '');
    setData({ ...data, mobile: cleanValue });
    setErrors((prev) => ({ ...prev, mobile: null }));
  };

  const sendOtpHandle = async (mobile) => {
    if (!mobile || mobile.length !== 10) {
      setErrors({ mobile: 'Please enter a valid 10-digit mobile number.' });
      return;
    }
    setErrors({});
    setOtpSendProgress(true);
    try {
      const response = await axios.post(`seller/otp/send`, { mobile: mobile });
      if (response.data.success) {
        setcounterVisible(true);
        setSendBtnVisible(false);
        setOtpSendProgress(false);
        toast.info('OTP sent successfully to your mobile number.');
      } else {
        setOtpSendProgress(false);
        toast.error('Failed to send OTP. Please try again.');
      }
    } catch (error) {
      setOtpSendProgress(false);
      const errorMsg = error.response?.data?.message || 'Error sending OTP';
      setErrors({ mobile: errorMsg });
      toast.error(errorMsg);
    }
  };

  const reSendOtpHandle = () => {
    setcounter(10);
    sendOtpHandle(data.mobile);
  };

  const sendOtpVerify = async () => {
    if (!data.otp || data.otp.length !== 6) {
      setErrors({ otp: 'Please enter a valid 6-digit OTP.' });
      return;
    }
    setOtpVerifyProgress(true);
    try {
      const response = await axios.post(`seller/otp/verify`, { mobile: data.mobile, otp: data.otp });
      if (response.data.success) {
        setOtpVerified(true);
        setOtpVerifyProgress(false);
        toast.success('Mobile number verified successfully!');
      } else {
        setOtpVerifyProgress(false);
        toast.error('Invalid OTP. Please check and try again.');
      }
    } catch (error) {
      setOtpVerifyProgress(false);
      if (error.response?.status === 400) {
        setErrors(error.response.data.errors);
      } else if (error.response?.status === 412) {
        setErrors({ otp: error.response.data.message || 'Incorrect OTP' });
        toast.error(error.response.data.message || 'Incorrect OTP');
      } else {
        toast.error('An error occurred during verification.');
      }
    }
  };

  // --- Dropdown API Logic ---
  const fetchState = async () => {
    try {
      const response = await axios.get(`/seller/masters/state/list`);
      if (response.data.success) setFetchStates(response.data.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchDistrict = async (state_id) => {
    setSelectedStateId(state_id);
    try {
      const response = await axios.post(`/seller/district/dropdown/list`, { state_id });
      setFetchDistricts(response.data.data);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchCity = async (district) => {
    try {
      const response = await axios.get(`/seller/masters/city/list/${selectedStateId}/${district}`);
      if (response.data.success) setFetchCities(response.data.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const handleState = (state) => {
    setData({ ...data, state: state, district: '', city: '' });
    setFetchDistricts([]);
    setFetchCities([]);
    if (state) {
      fetchDistrict(state);
    }
  };

  const handleDistrict = (district) => {
    setData({ ...data, district: district, city: '' });
    setFetchCities([]);
    if (district) {
      fetchCity(district);
    }
  };

  const fetchBusinessCategory = async () => {
    try {
      const response = await axios.get(`seller/masters/business/category`);

      if (response.data.success) setFetchBusinessCat(response.data.data);
    } catch (error) {
      console.error('Error fetching business categories:', error);
    }
  };

  // --- Document File Change Handlers ---
  const handleFileChange = (e, fieldName) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, [fieldName]: 'File size must not exceed 5MB' }));
        toast.error('File is too large! Maximum allowed size is 5MB.');
        return;
      }
      setErrors(prev => ({ ...prev, [fieldName]: null }));
      if (selectedFile.type === 'application/pdf') {
        setFilePreview({ ...filePreview, [fieldName]: 'pdf' });
      } else {
        setFilePreview({ ...filePreview, [fieldName]: URL.createObjectURL(selectedFile) });
      }
      setFile({ ...file, [fieldName]: selectedFile });
    }
  };

  // --- Validation & Smooth Scroll ---
  const scrollToFirstError = (errorFields) => {
    const fieldNames = Object.keys(errorFields);
    if (fieldNames.length > 0) {
      const firstErrorField = fieldNames[0];
      // Try to find the element
      const element = fieldRefs.current[firstErrorField];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          element.focus();
        }, 500);
      }
    }
  };

  const validateForm = () => {
    const validationErrors = {};

    // Basic Details
    if (!data.name || data.name.trim() === '') validationErrors.name = 'Full Name is required.';
    if (!data.password || data.password.trim() === '') {
      validationErrors.password = 'Password is required.';
    } else if (data.password.length < 8) {
      validationErrors.password = 'Password must be at least 8 characters long.';
    }
    if (data.password !== data.password_confirmation) {
      validationErrors.password_confirmation = 'Confirm Password must match Password.';
    }

    // Business Details
    if (!data.bisiness_name || data.bisiness_name.trim() === '') validationErrors.bisiness_name = 'Business Name is required.';
    if (!data.business_category) validationErrors.business_category = 'Please select a Business Type.';
    if (!data.trade_lic_no || data.trade_lic_no.trim() === '') validationErrors.trade_lic_no = 'Trade License Number is required.';
    if (!data.pan_no || data.pan_no.trim() === '') validationErrors.pan_no = 'PAN Number is required.';
    if (!data.state) validationErrors.state = 'Please select a State.';
    if (!data.district) validationErrors.district = 'Please select a District.';
    if (!data.city) validationErrors.city = 'Please select a City.';
    if (!data.pin || data.pin.trim() === '') {
      validationErrors.pin = 'PIN Code is required.';
    } else if (!/^\d{6}$/.test(data.pin)) {
      validationErrors.pin = 'PIN Code must be exactly 6 digits.';
    }

    // Bank Details
    if (!data.name_of_ac_owner || data.name_of_ac_owner.trim() === '') validationErrors.name_of_ac_owner = 'Account Owner Name is required.';
    if (!data.ac_no || data.ac_no.trim() === '') validationErrors.ac_no = 'Bank Account Number is required.';
    if (!data.bank_name || data.bank_name.trim() === '') validationErrors.bank_name = 'Bank Name is required.';
    if (!data.bank_branch || data.bank_branch.trim() === '') validationErrors.bank_branch = 'Branch Name is required.';
    if (!data.ifsc_code || data.ifsc_code.trim() === '') validationErrors.ifsc_code = 'IFSC Code is required.';

    // Documents
    if (!file.pan_image) validationErrors.pan_image = 'PAN Card image/PDF file is required.';
    if (!file.trade_lic_image) validationErrors.trade_lic_image = 'Trade License image/PDF file is required.';

    return validationErrors;
  };

  const handleInputChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleInputBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // Submit Logic
  const handleSubmitAll = async () => {
    setErrors({});
    setAlertMessage('');

    // Run frontend validation
    const frontendErrors = validateForm();
    if (Object.keys(frontendErrors).length > 0) {
      setErrors(frontendErrors);
      scrollToFirstError(frontendErrors);
      setAlertMessage('Please check the fields highlighted in red below.');
      toast.error('Registration failed. Please correct the highlighted errors.');
      return;
    }

    try {
      setLoading(true);
      setLoadingMsg('Submitting your registration form... Please do not refresh.');

      const formData = new FormData();
      // Basic Details
      formData.append('mobile', data.mobile);
      formData.append('name', data.name);
      if (data.email) formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('password_confirmation', data.password_confirmation);

      // Business Details
      formData.append('bisiness_name', data.bisiness_name);
      formData.append('trade_lic_no', data.trade_lic_no);
      formData.append('pan_no', data.pan_no);
      if (data.gst_no) formData.append('gst_no', data.gst_no);
      formData.append('state', data.state);
      formData.append('district', data.district);
      formData.append('city', data.city);
      formData.append('pin', data.pin);
      if (data.street) formData.append('street', data.street);
      if (data.land_mark) formData.append('land_mark', data.land_mark);
      formData.append('business_mobile', data.business_mobile || data.mobile);
      if (data.alt_mobile) formData.append('alt_mobile', data.alt_mobile);
      if (data.address) formData.append('address', data.address);
      formData.append('business_category', data.business_category);

      // Bank Details
      formData.append('name_of_ac_owner', data.name_of_ac_owner);
      formData.append('ac_no', data.ac_no);
      formData.append('ifsc_code', data.ifsc_code);
      formData.append('bank_name', data.bank_name);
      formData.append('bank_branch', data.bank_branch);
      if (data.business_email) formData.append('business_email', data.business_email);
      if (data.business_description) formData.append('business_description', data.business_description);

      // Documents
      if (file.pan_image) formData.append('pan_image', file.pan_image);
      if (file.trade_lic_image) formData.append('trade_lic_image', file.trade_lic_image);
      if (file.gst_image) formData.append('gst_image', file.gst_image);

      const response = await axios.post(`seller/registration/register-all`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        }
      });

      setLoading(false);

      if (response.data.success) {
        toast.success('Registration completed successfully!');
        navigate('/registration/registrationApprovalPage');
      } else {
        setAlertMessage(response.data.message || 'Registration failed.');
      }

    } catch (error) {
      setLoading(false);
      console.error('Registration Submission Error', error);
      if (error.response && error.response.status === 400) {
        const backendErrors = error.response.data.errors;
        // The backend returns validation errors in an object
        // Normalize error format (backend returns array of strings per field, we take the first message)
        const parsedErrors = {};
        Object.keys(backendErrors).forEach((key) => {
          if (Array.isArray(backendErrors[key])) {
            parsedErrors[key] = backendErrors[key][0];
          } else {
            parsedErrors[key] = backendErrors[key];
          }
        });
        setErrors(parsedErrors);
        setAlertMessage('Please fix the errors returned by the server highlighted below.');
        scrollToFirstError(parsedErrors);
        toast.error('Please correct the validation errors reported by the server.');
      } else {
        const errorMsg = error.response?.data?.message || 'An unexpected error occurred. Please try again.';
        setAlertMessage(errorMsg);
        toast.error(errorMsg);
      }
    }
  };

  const getSectionStatus = (section) => {
    switch (section) {
      case 'personal':
        return data.name && data.password && data.password === data.password_confirmation;
      case 'business':
        return data.bisiness_name && data.business_category && data.trade_lic_no && data.pan_no;
      case 'address':
        return data.state && data.district && data.city && data.pin;
      case 'bank':
        return data.name_of_ac_owner && data.ac_no && data.bank_name && data.bank_branch && data.ifsc_code;
      case 'docs':
        return file.pan_image && file.trade_lic_image;
      default:
        return false;
    }
  };

  return (
    <div style={styles.pageBackground}>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />

      {/* Embedded CSS Style Tag for Premium Interactions */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .senior-input {
          height: 58px;
          font-size: 1.15rem !important;
          padding: 12px 20px !important;
          border: 2px solid #8e9cae !important;
          border-radius: 12px !important;
          transition: all 0.3s ease;
          color: #1e293b;
          font-weight: 500;
        }
        .senior-input:focus {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.2) !important;
          outline: none;
          background-color: #fbfcfe;
        }
        .senior-input.is-invalid {
          border-color: #ef4444 !important;
          background-color: #fef2f2 !important;
        }
        .senior-input.is-valid {
          border-color: #22c55e !important;
          background-color: #f0fdf4 !important;
        }
        .senior-label {
          font-size: 1.15rem !important;
          font-weight: 700 !important;
          color: #1e293b;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
        }
        .senior-help {
          font-size: 0.95rem;
          color: #64748b;
          margin-bottom: 8px;
          display: block;
        }
        .error-message {
          font-size: 1rem;
          font-weight: 600;
          color: #ef4444;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .card-custom {
          border-radius: 20px !important;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05) !important;
          border: none !important;
          margin-bottom: 30px;
          background-color: #ffffff;
        }
        .btn-senior {
          padding: 15px 30px !important;
          font-size: 1.25rem !important;
          font-weight: 700 !important;
          border-radius: 50px !important;
          transition: all 0.3s ease;
        }
        .btn-senior:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3) !important;
        }
        .section-header {
          border-bottom: 2px solid #f1f5f9;
          padding-bottom: 15px;
          margin-bottom: 25px;
          color: #1e3a8a;
          font-weight: 800;
        }
        .doc-card {
          border: 3px dashed #cbd5e1;
          border-radius: 16px;
          padding: 25px;
          text-align: center;
          background-color: #f8fafc;
          transition: all 0.3s ease;
          position: relative;
          cursor: pointer;
        }
        .doc-card:hover {
          border-color: #2563eb;
          background-color: #f1f5f9;
        }
        .doc-card.has-error {
          border-color: #ef4444;
          background-color: #fef2f2;
        }
        .doc-preview {
          max-height: 140px;
          object-fit: contain;
          border-radius: 8px;
          margin-bottom: 15px;
        }
        .sticky-nav {
          position: sticky;
          top: 100px;
          z-index: 100;
        }
        .nav-item-custom {
          background-color: #ffffff;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 10px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.03);
          border-left: 5px solid #cbd5e1;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .nav-item-custom:hover {
          background-color: #f8fafc;
          transform: translateX(3px);
        }
        .nav-item-custom.active {
          border-left-color: #2563eb;
          background-color: #eff6ff;
        }
      `}} />

      {/* Loading Overlay */}
      {(loading || otpSendProgress || otpVerifyProgress) && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingBox}>
            <Spinner animation="border" variant="primary" style={{ width: '3.5rem', height: '3.5rem' }} />
            <h3 className="mt-4 fw-bold text-dark">{loadingMsg || 'Verifying Details...'}</h3>
            <p className="text-muted mb-0 mt-2">Please wait, do not close or refresh this page.</p>
          </div>
        </div>
      )}

      {/* Top Header Banner */}
      <div style={styles.headerBanner} className="shadow-sm">
        <div className="container d-flex justify-content-between align-items-center" style={{ maxWidth: '1200px' }}>
          <div>
            <img src="/logo.webp" alt="AiviMart Logo" style={{ height: '48px' }} />
          </div>
          <div>
            <span style={{ fontSize: '1.05rem', color: '#64748b' }} className="me-3 d-none d-sm-inline">Already registered?</span>
            <Link to="/login" className="btn btn-outline-primary btn-senior py-2 px-4 fs-6" style={{ borderRadius: '30px' }}>
              Sign In Here
            </Link>
          </div>
        </div>
      </div>

      <div className="container py-5" style={{ maxWidth: '1200px' }}>
        <div className="text-center mb-5">
          <h1 style={styles.mainTitle}>Seller Registration Form</h1>
          <p style={styles.subTitle}>Fill out this single page form to register as an AiviMart Seller and start selling.</p>
        </div>

        {alertMessage && (
          <div className="alert alert-danger p-4 mb-4 shadow-sm" style={{ borderRadius: '15px', borderLeft: '6px solid #ef4444' }}>
            <div className="d-flex align-items-center gap-3">
              <span style={{ fontSize: '2rem' }}>⚠️</span>
              <div>
                <h5 className="fw-bold mb-1">Attention Required</h5>
                <p className="mb-0 text-danger fw-semibold" style={{ fontSize: '1.05rem' }}>{alertMessage}</p>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          {/* Sticky Navigation Sidebar (Only displays after OTP verification) */}
          {otpVerified && (
            <div className="col-lg-3 d-none d-lg-block">
              <div className="sticky-nav">
                <h5 className="fw-bold mb-3 text-dark px-2">Registration Sections</h5>

                <div className="nav-item-custom active" onClick={() => scrollToFirstError({ name: true })}>
                  <span className="fw-bold">1. Personal Info</span>
                  <span>{getSectionStatus('personal') ? '✅' : '⏳'}</span>
                </div>

                <div className="nav-item-custom active" onClick={() => scrollToFirstError({ bisiness_name: true })}>
                  <span className="fw-bold">2. Business Info</span>
                  <span>{getSectionStatus('business') ? '✅' : '⏳'}</span>
                </div>

                <div className="nav-item-custom active" onClick={() => scrollToFirstError({ state: true })}>
                  <span className="fw-bold">3. Address Info</span>
                  <span>{getSectionStatus('address') ? '✅' : '⏳'}</span>
                </div>

                <div className="nav-item-custom active" onClick={() => scrollToFirstError({ name_of_ac_owner: true })}>
                  <span className="fw-bold">4. Bank Account</span>
                  <span>{getSectionStatus('bank') ? '✅' : '⏳'}</span>
                </div>

                <div className="nav-item-custom active" onClick={() => scrollToFirstError({ pan_image: true })}>
                  <span className="fw-bold">5. Documents</span>
                  <span>{getSectionStatus('docs') ? '✅' : '⏳'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Form Content Column */}
          <div className={otpVerified ? 'col-lg-9' : 'col-12 mx-auto'} style={{ maxWidth: otpVerified ? 'none' : '750px' }}>

            {/* --- SECTION 1: MOBILE VERIFICATION --- */}
            <div className="card card-custom shadow-sm">
              <div className="card-body p-4 p-md-5">
                <h3 className="section-header d-flex align-items-center gap-2">
                  <span style={styles.stepBadge}>1</span>
                  Mobile Phone Verification
                </h3>
                <p className="text-muted mb-4" style={{ fontSize: '1.05rem' }}>
                  Older Sellers, please enter your mobile phone number. We will send you a text message with a code to verify it is you.
                </p>

                <div className="row align-items-end">
                  <div className="col-md-7 mb-3">
                    <label className="senior-label" htmlFor="mobile_input">
                      Mobile Number <span className="text-danger ms-1">*</span>
                    </label>
                    <span className="senior-help">Type your 10-digit phone number (without +91 or 0)</span>
                    <input
                      id="mobile_input"
                      ref={(el) => (fieldRefs.current.mobile = el)}
                      type="text"
                      maxLength="10"
                      className={`form-control senior-input ${errors.mobile ? 'is-invalid' : ''} ${otpVerified ? 'is-valid' : ''}`}
                      placeholder="e.g. 9876543210"
                      value={data.mobile}
                      onChange={(e) => handleMobileChange(e.target.value)}
                      disabled={otpVerified}
                      style={{ letterSpacing: '2px', fontWeight: 'bold' }}
                    />
                    {errors.mobile && (
                      <div className="error-message">
                        <span>❌</span> {errors.mobile}
                      </div>
                    )}
                  </div>

                  <div className="col-md-5 mb-3">
                    {!otpVerified && sendBtnVisible && (
                      <button
                        className="btn btn-primary btn-senior w-100"
                        type="button"
                        onClick={() => sendOtpHandle(data.mobile)}
                        disabled={data.mobile.length !== 10}
                      >
                        📞 Send Code (OTP)
                      </button>
                    )}
                    {otpVerified && (
                      <div className="alert alert-success py-3 px-4 mb-0 d-flex align-items-center gap-2" style={{ borderRadius: '12px' }}>
                        <span style={{ fontSize: '1.4rem' }}>✅</span>
                        <strong style={{ fontSize: '1.1rem' }}>Phone Verified</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* OTP Input Block */}
                {!otpVerified && counterVisible && (
                  <div className="bg-light p-4 rounded-3 border mt-4">
                    <h5 className="fw-bold mb-3 text-dark">Enter Code Sent to Your Mobile</h5>
                    <div className="row align-items-center g-3">
                      <div className="col-sm-6">
                        <input
                          type="text"
                          maxLength="6"
                          className={`form-control senior-input text-center ${errors.otp ? 'is-invalid' : ''}`}
                          placeholder="6-digit OTP code"
                          value={data.otp}
                          onChange={(e) => setData({ ...data, otp: e.target.value.replace(/[^0-9]/g, '') })}
                          style={{ letterSpacing: '4px', fontWeight: 'bold', fontSize: '1.4rem' }}
                        />
                        {errors.otp && (
                          <div className="error-message">
                            <span>❌</span> {errors.otp}
                          </div>
                        )}
                      </div>
                      <div className="col-sm-6 d-flex align-items-center gap-2">
                        <button className="btn btn-success btn-senior w-100 py-3 fs-6" type="button" onClick={sendOtpVerify}>
                          🔑 Verify Code
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 text-muted fw-semibold d-flex align-items-center gap-2">
                      <span>Didn't get code?</span>
                      <OtpTimer
                        seconds={counter}
                        buttonColor={'#2563eb'}
                        background={'transparent'}
                        textColor={'#475569'}
                        ButtonText="Resend Code Now"
                        text=""
                        minutes={0}
                        resend={reSendOtpHandle}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* --- COMPLETE FORM SECTIONS: DISPLAYED ONLY IF MOBILE IS VERIFIED --- */}
            {otpVerified && (
              <>
                {/* --- SECTION 2: PERSONAL/BASIC DETAILS --- */}
                <div className="card card-custom shadow-sm">
                  <div className="card-body p-4 p-md-5">
                    <h3 className="section-header d-flex align-items-center gap-2">
                      <span style={styles.stepBadge}>2</span>
                      Personal Details
                    </h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem' }}>
                      Enter your personal information to create your secure seller login account.
                    </p>

                    <div className="row g-4">
                      {/* Name */}
                      <div className="col-12">
                        <label className="senior-label" htmlFor="name">
                          Full Name <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Enter your official name (as written on bank account/ID cards)</span>
                        <input
                          id="name"
                          ref={(el) => (fieldRefs.current.name = el)}
                          type="text"
                          className={`form-control senior-input ${errors.name ? 'is-invalid' : ''}`}
                          placeholder="e.g. Ram Prasad Sharma"
                          value={data.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          onBlur={() => handleInputBlur('name')}
                        />
                        {errors.name && (
                          <div className="error-message">
                            <span>❌</span> {errors.name}
                          </div>
                        )}
                      </div>

                      {/* Email */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="email">
                          Personal Email Address <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">E.g., name@gmail.com. Used to send invoices/bills</span>
                        <input
                          id="email"
                          ref={(el) => (fieldRefs.current.email = el)}
                          type="email"
                          className={`form-control senior-input ${errors.email ? 'is-invalid' : ''}`}
                          placeholder="e.g. ram@gmail.com"
                          value={data.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                        />
                        {errors.email && (
                          <div className="error-message">
                            <span>❌</span> {errors.email}
                          </div>
                        )}
                      </div>

                      {/* Alternate Mobile */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="alt_mobile">
                          Alternate Mobile Number <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">Another number in case your phone is busy</span>
                        <input
                          id="alt_mobile"
                          ref={(el) => (fieldRefs.current.alt_mobile = el)}
                          type="text"
                          maxLength="10"
                          className={`form-control senior-input ${errors.alt_mobile ? 'is-invalid' : ''}`}
                          placeholder="e.g. 9876543211"
                          value={data.alt_mobile}
                          onChange={(e) => handleInputChange('alt_mobile', e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        {errors.alt_mobile && (
                          <div className="error-message">
                            <span>❌</span> {errors.alt_mobile}
                          </div>
                        )}
                      </div>

                      {/* Password */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="password">
                          Account Password <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Create a strong password (minimum 8 letters, with numbers & symbols)</span>
                        <input
                          id="password"
                          ref={(el) => (fieldRefs.current.password = el)}
                          type="password"
                          className={`form-control senior-input ${errors.password ? 'is-invalid' : ''}`}
                          placeholder="Enter strong password"
                          value={data.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                        />
                        {errors.password && (
                          <div className="error-message">
                            <span>❌</span> {errors.password}
                          </div>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="password_confirmation">
                          Confirm Password <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Retype the same password to make sure there are no typos</span>
                        <input
                          id="password_confirmation"
                          ref={(el) => (fieldRefs.current.password_confirmation = el)}
                          type="password"
                          className={`form-control senior-input ${errors.password_confirmation ? 'is-invalid' : ''}`}
                          placeholder="Re-enter password"
                          value={data.password_confirmation}
                          onChange={(e) => handleInputChange('password_confirmation', e.target.value)}
                        />
                        {errors.password_confirmation && (
                          <div className="error-message">
                            <span>❌</span> {errors.password_confirmation}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- SECTION 3: BUSINESS DETAILS --- */}
                <div className="card card-custom shadow-sm">
                  <div className="card-body p-4 p-md-5">
                    <h3 className="section-header d-flex align-items-center gap-2">
                      <span style={styles.stepBadge}>3</span>
                      Business Information
                    </h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem' }}>
                      Enter your store name, business type, and registration numbers.
                    </p>

                    <div className="row g-4">
                      {/* Business Name */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="bisiness_name">
                          Shop / Business Name <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">E.g., Sharma Grocery Stores</span>
                        <input
                          id="bisiness_name"
                          ref={(el) => (fieldRefs.current.bisiness_name = el)}
                          type="text"
                          className={`form-control senior-input ${errors.bisiness_name ? 'is-invalid' : ''}`}
                          placeholder="e.g. Sharma Grocery Stores"
                          value={data.bisiness_name}
                          onChange={(e) => handleInputChange('bisiness_name', e.target.value)}
                        />
                        {errors.bisiness_name && (
                          <div className="error-message">
                            <span>❌</span> {errors.bisiness_name}
                          </div>
                        )}
                      </div>

                      {/* Business Type / Category */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="business_category">
                          Business Type <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Select the main category of items you sell</span>
                        <select
                          id="business_category"
                          ref={(el) => (fieldRefs.current.business_category = el)}
                          className={`form-select senior-input ${errors.business_category ? 'is-invalid' : ''}`}
                          value={data.business_category}
                          onChange={(e) => handleInputChange('business_category', e.target.value)}
                        >
                          <option value="">-- Choose Business Type --</option>
                          {fetchBusinessCat.map((item, i) => (
                            <option key={i} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                        {errors.business_category && (
                          <div className="error-message">
                            <span>❌</span> {errors.business_category}
                          </div>
                        )}
                      </div>

                      {/* Business Email */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="business_email">
                          Business Email Address <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">Store support/billing email</span>
                        <input
                          id="business_email"
                          ref={(el) => (fieldRefs.current.business_email = el)}
                          type="email"
                          className={`form-control senior-input ${errors.business_email ? 'is-invalid' : ''}`}
                          placeholder="e.g. support@sharmastores.com"
                          value={data.business_email}
                          onChange={(e) => handleInputChange('business_email', e.target.value)}
                        />
                        {errors.business_email && (
                          <div className="error-message">
                            <span>❌</span> {errors.business_email}
                          </div>
                        )}
                      </div>

                      {/* Business Mobile */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="business_mobile">
                          Business Mobile Number <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">Store hotline number. Will default to main mobile if blank.</span>
                        <input
                          id="business_mobile"
                          ref={(el) => (fieldRefs.current.business_mobile = el)}
                          type="text"
                          maxLength="10"
                          className={`form-control senior-input ${errors.business_mobile ? 'is-invalid' : ''}`}
                          placeholder="e.g. 9876543210"
                          value={data.business_mobile}
                          onChange={(e) => handleInputChange('business_mobile', e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        {errors.business_mobile && (
                          <div className="error-message">
                            <span>❌</span> {errors.business_mobile}
                          </div>
                        )}
                      </div>

                      {/* Business Description */}
                      <div className="col-12">
                        <label className="senior-label" htmlFor="business_description">
                          About Your Business <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">Write a short note about what you sell (e.g., We sell fresh farm vegetables and groceries)</span>
                        <textarea
                          id="business_description"
                          ref={(el) => (fieldRefs.current.business_description = el)}
                          rows="3"
                          className={`form-control senior-input ${errors.business_description ? 'is-invalid' : ''}`}
                          placeholder="Tell us a little bit about what your shop sells..."
                          value={data.business_description}
                          onChange={(e) => handleInputChange('business_description', e.target.value)}
                          style={{ height: 'auto' }}
                        ></textarea>
                        {errors.business_description && (
                          <div className="error-message">
                            <span>❌</span> {errors.business_description}
                          </div>
                        )}
                      </div>

                      {/* Tax Information Sub-heading */}
                      <div className="col-12 mt-4">
                        <h4 className="fw-bold text-primary mb-2" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                          📄 Tax Information
                        </h4>
                        <p className="text-muted small">We require these government tax registration codes to verify your store status.</p>
                      </div>

                      {/* Trade License */}
                      <div className="col-md-6 col-12">
                        <label className="senior-label" htmlFor="trade_lic_no">
                          Trade License Number <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Government municipal trade license registration number</span>
                        <input
                          id="trade_lic_no"
                          ref={(el) => (fieldRefs.current.trade_lic_no = el)}
                          type="text"
                          className={`form-control senior-input ${errors.trade_lic_no ? 'is-invalid' : ''}`}
                          placeholder="Enter license number"
                          value={data.trade_lic_no}
                          onChange={(e) => handleInputChange('trade_lic_no', e.target.value)}
                        />
                        {errors.trade_lic_no && (
                          <div className="error-message">
                            <span>❌</span> {errors.trade_lic_no}
                          </div>
                        )}
                      </div>

                      {/* PAN Number */}
                      <div className="col-md-6 col-12">
                        <label className="senior-label" htmlFor="pan_no">
                          PAN Number <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Permanent Account Number (10 digits/letters)</span>
                        <input
                          id="pan_no"
                          ref={(el) => (fieldRefs.current.pan_no = el)}
                          type="text"
                          maxLength="10"
                          className={`form-control senior-input ${errors.pan_no ? 'is-invalid' : ''}`}
                          placeholder="e.g. ABCDE1234F"
                          value={data.pan_no}
                          onChange={(e) => handleInputChange('pan_no', e.target.value.toUpperCase())}
                          style={{ textTransform: 'uppercase' }}
                        />
                        {errors.pan_no && (
                          <div className="error-message">
                            <span>❌</span> {errors.pan_no}
                          </div>
                        )}
                      </div>

                      {/* GST Number */}
                      <div className="col-md-6 col-12">
                        <label className="senior-label" htmlFor="gst_no">
                          GST Number <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">Goods & Services Tax number (15 characters)</span>
                        <input
                          id="gst_no"
                          ref={(el) => (fieldRefs.current.gst_no = el)}
                          type="text"
                          maxLength="15"
                          className={`form-control senior-input ${errors.gst_no ? 'is-invalid' : ''}`}
                          placeholder="e.g. 18AABCU9603R1ZM"
                          value={data.gst_no}
                          onChange={(e) => handleInputChange('gst_no', e.target.value.toUpperCase())}
                          style={{ textTransform: 'uppercase' }}
                        />
                        {errors.gst_no && (
                          <div className="error-message">
                            <span>❌</span> {errors.gst_no}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- SECTION 4: ADDRESS DETAILS --- */}
                <div className="card card-custom shadow-sm">
                  <div className="card-body p-4 p-md-5">
                    <h3 className="section-header d-flex align-items-center gap-2">
                      <span style={styles.stepBadge}>4</span>
                      Address Details
                    </h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem' }}>
                      Enter the address of your shop/store.
                    </p>

                    <div className="row g-4">
                      {/* State */}
                      <div className="col-12 col-md-4">
                        <label className="senior-label" htmlFor="state">
                          State <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Select your State</span>
                        <select
                          id="state"
                          ref={(el) => (fieldRefs.current.state = el)}
                          className={`form-select senior-input ${errors.state ? 'is-invalid' : ''}`}
                          value={data.state}
                          onChange={(e) => handleState(e.target.value)}
                        >
                          <option value="">-- Choose State --</option>
                          {fetchStates?.map((item, i) => (
                            <option key={i} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                        {errors.state && (
                          <div className="error-message">
                            <span>❌</span> {errors.state}
                          </div>
                        )}
                      </div>

                      {/* District */}
                      <div className="col-12 col-md-4">
                        <label className="senior-label" htmlFor="district">
                          District <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Select your District</span>
                        <select
                          id="district"
                          ref={(el) => (fieldRefs.current.district = el)}
                          className={`form-select senior-input ${errors.district ? 'is-invalid' : ''}`}
                          value={data.district}
                          onChange={(e) => handleDistrict(e.target.value)}
                          disabled={!data.state}
                        >
                          <option value="">-- Choose District --</option>
                          {fetchDistricts?.map((item, i) => (
                            <option key={i} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                        {errors.district && (
                          <div className="error-message">
                            <span>❌</span> {errors.district}
                          </div>
                        )}
                      </div>

                      {/* City */}
                      <div className="col-12 col-md-4">
                        <label className="senior-label" htmlFor="city">
                          City <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Select your City</span>
                        <select
                          id="city"
                          ref={(el) => (fieldRefs.current.city = el)}
                          className={`form-select senior-input ${errors.city ? 'is-invalid' : ''}`}
                          value={data.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          disabled={!data.district}
                        >
                          <option value="">-- Choose City --</option>
                          {fetchCities?.map((item, i) => (
                            <option key={i} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                        {errors.city && (
                          <div className="error-message">
                            <span>❌</span> {errors.city}
                          </div>
                        )}
                      </div>

                      {/* PIN Code */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="pin">
                          PIN Code <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">6-digit postal code of your shop area</span>
                        <input
                          id="pin"
                          ref={(el) => (fieldRefs.current.pin = el)}
                          type="text"
                          maxLength="6"
                          className={`form-control senior-input ${errors.pin ? 'is-invalid' : ''}`}
                          placeholder="e.g. 781001"
                          value={data.pin}
                          onChange={(e) => handleInputChange('pin', e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        {errors.pin && (
                          <div className="error-message">
                            <span>❌</span> {errors.pin}
                          </div>
                        )}
                      </div>

                      {/* Street */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="street">
                          Street / Road Name <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">E.g., Mahatma Gandhi Road, Ward 12</span>
                        <input
                          id="street"
                          ref={(el) => (fieldRefs.current.street = el)}
                          type="text"
                          className={`form-control senior-input ${errors.street ? 'is-invalid' : ''}`}
                          placeholder="e.g. MG Road, Near Market"
                          value={data.street}
                          onChange={(e) => handleInputChange('street', e.target.value)}
                        />
                        {errors.street && (
                          <div className="error-message">
                            <span>❌</span> {errors.street}
                          </div>
                        )}
                      </div>

                      {/* Landmark */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="land_mark">
                          Landmark <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">A well-known spot nearby (e.g. Opposite Post Office)</span>
                        <input
                          id="land_mark"
                          ref={(el) => (fieldRefs.current.land_mark = el)}
                          type="text"
                          className={`form-control senior-input ${errors.land_mark ? 'is-invalid' : ''}`}
                          placeholder="e.g. Near SBI Bank"
                          value={data.land_mark}
                          onChange={(e) => handleInputChange('land_mark', e.target.value)}
                        />
                        {errors.land_mark && (
                          <div className="error-message">
                            <span>❌</span> {errors.land_mark}
                          </div>
                        )}
                      </div>

                      {/* Full Address */}
                      <div className="col-12">
                        <label className="senior-label" htmlFor="address">
                          Full Physical Address <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">Write details like Shop No, Building name, Village/Area</span>
                        <textarea
                          id="address"
                          ref={(el) => (fieldRefs.current.address = el)}
                          rows="2"
                          className={`form-control senior-input ${errors.address ? 'is-invalid' : ''}`}
                          placeholder="Enter complete store address..."
                          value={data.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          style={{ height: 'auto' }}
                        ></textarea>
                        {errors.address && (
                          <div className="error-message">
                            <span>❌</span> {errors.address}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- SECTION 5: BANK DETAILS --- */}
                <div className="card card-custom shadow-sm">
                  <div className="card-body p-4 p-md-5">
                    <h3 className="section-header d-flex align-items-center gap-2">
                      <span style={styles.stepBadge}>5</span>
                      Bank Account Details
                    </h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem' }}>
                      Enter the bank account details where you want to receive payments for your sales.
                    </p>

                    <div className="row g-4">
                      {/* Name of A/C Owner */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="name_of_ac_owner">
                          Account Holder Name <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Name exactly as written on your bank passbook/statement</span>
                        <input
                          id="name_of_ac_owner"
                          ref={(el) => (fieldRefs.current.name_of_ac_owner = el)}
                          type="text"
                          className={`form-control senior-input ${errors.name_of_ac_owner ? 'is-invalid' : ''}`}
                          placeholder="e.g. Ram Prasad Sharma"
                          value={data.name_of_ac_owner}
                          onChange={(e) => handleInputChange('name_of_ac_owner', e.target.value)}
                        />
                        {errors.name_of_ac_owner && (
                          <div className="error-message">
                            <span>❌</span> {errors.name_of_ac_owner}
                          </div>
                        )}
                      </div>

                      {/* Account Number */}
                      <div className="col-12 col-md-6">
                        <label className="senior-label" htmlFor="ac_no">
                          Bank Account Number <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Enter your bank account number</span>
                        <input
                          id="ac_no"
                          ref={(el) => (fieldRefs.current.ac_no = el)}
                          type="text"
                          className={`form-control senior-input ${errors.ac_no ? 'is-invalid' : ''}`}
                          placeholder="e.g. 10023456789"
                          value={data.ac_no}
                          onChange={(e) => handleInputChange('ac_no', e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        {errors.ac_no && (
                          <div className="error-message">
                            <span>❌</span> {errors.ac_no}
                          </div>
                        )}
                      </div>

                      {/* Bank Name */}
                      <div className="col-12 col-md-4">
                        <label className="senior-label" htmlFor="bank_name">
                          Bank Name <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">E.g., State Bank of India</span>
                        <input
                          id="bank_name"
                          ref={(el) => (fieldRefs.current.bank_name = el)}
                          type="text"
                          className={`form-control senior-input ${errors.bank_name ? 'is-invalid' : ''}`}
                          placeholder="e.g. State Bank of India"
                          value={data.bank_name}
                          onChange={(e) => handleInputChange('bank_name', e.target.value)}
                        />
                        {errors.bank_name && (
                          <div className="error-message">
                            <span>❌</span> {errors.bank_name}
                          </div>
                        )}
                      </div>

                      {/* Bank Branch */}
                      <div className="col-12 col-md-4">
                        <label className="senior-label" htmlFor="bank_branch">
                          Branch Name <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">E.g., Silpukhuri Branch</span>
                        <input
                          id="bank_branch"
                          ref={(el) => (fieldRefs.current.bank_branch = el)}
                          type="text"
                          className={`form-control senior-input ${errors.bank_branch ? 'is-invalid' : ''}`}
                          placeholder="e.g. Silpukhuri"
                          value={data.bank_branch}
                          onChange={(e) => handleInputChange('bank_branch', e.target.value)}
                        />
                        {errors.bank_branch && (
                          <div className="error-message">
                            <span>❌</span> {errors.bank_branch}
                          </div>
                        )}
                      </div>

                      {/* IFSC Code */}
                      <div className="col-12 col-md-4">
                        <label className="senior-label" htmlFor="ifsc_code">
                          IFSC Code <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">11-character bank code (e.g. SBIN0001234)</span>
                        <input
                          id="ifsc_code"
                          ref={(el) => (fieldRefs.current.ifsc_code = el)}
                          type="text"
                          maxLength="11"
                          className={`form-control senior-input ${errors.ifsc_code ? 'is-invalid' : ''}`}
                          placeholder="e.g. SBIN0001234"
                          value={data.ifsc_code}
                          onChange={(e) => handleInputChange('ifsc_code', e.target.value.toUpperCase())}
                          style={{ textTransform: 'uppercase' }}
                        />
                        {errors.ifsc_code && (
                          <div className="error-message">
                            <span>❌</span> {errors.ifsc_code}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- SECTION 6: DOCUMENTS UPLOAD --- */}
                <div className="card card-custom shadow-sm">
                  <div className="card-body p-4 p-md-5">
                    <h3 className="section-header d-flex align-items-center gap-2">
                      <span style={styles.stepBadge}>6</span>
                      Upload Verification Documents
                    </h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem' }}>
                      Please upload clear photos or PDF copies of your documents. File size must be less than 5MB.
                    </p>

                    <div className="row g-4">
                      {/* PAN Card Image */}
                      <div className="col-md-6 col-12">
                        <label className="senior-label">
                          PAN Card Photo/PDF <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Upload copy of your PAN card</span>

                        <div
                          ref={(el) => (fieldRefs.current.pan_image = el)}
                          className={`doc-card ${errors.pan_image ? 'has-error' : ''}`}
                          onClick={() => document.getElementById('pan_image_input').click()}
                        >
                          <input
                            id="pan_image_input"
                            type="file"
                            accept="image/*,application/pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileChange(e, 'pan_image')}
                          />

                          {filePreview.pan_image ? (
                            filePreview.pan_image === 'pdf' ? (
                              <div className="py-4">
                                <span style={{ fontSize: '3rem' }}>📄</span>
                                <div className="fw-bold text-dark mt-2">PAN_Card.pdf</div>
                                <span className="badge bg-primary mt-2">PDF Document Ready</span>
                              </div>
                            ) : (
                              <div>
                                <img src={filePreview.pan_image} alt="PAN Preview" className="doc-preview" />
                                <div className="text-muted small mt-1">Click to change file</div>
                              </div>
                            )
                          ) : (
                            <div className="py-4">
                              <span style={{ fontSize: '3rem', color: '#94a3b8' }}>📁</span>
                              <h5 className="fw-bold mt-3 text-secondary">Click to upload PAN Card</h5>
                              <p className="text-muted small mb-0">Images (JPEG, PNG) or PDF (Max 5MB)</p>
                            </div>
                          )}
                        </div>
                        {errors.pan_image && (
                          <div className="error-message">
                            <span>❌</span> {errors.pan_image}
                          </div>
                        )}
                      </div>

                      {/* Trade License Image */}
                      <div className="col-md-6 col-12">
                        <label className="senior-label">
                          Trade License Photo/PDF <span className="text-danger ms-1">*</span>
                        </label>
                        <span className="senior-help">Upload copy of municipal trade license</span>

                        <div
                          ref={(el) => (fieldRefs.current.trade_lic_image = el)}
                          className={`doc-card ${errors.trade_lic_image ? 'has-error' : ''}`}
                          onClick={() => document.getElementById('trade_lic_image_input').click()}
                        >
                          <input
                            id="trade_lic_image_input"
                            type="file"
                            accept="image/*,application/pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileChange(e, 'trade_lic_image')}
                          />

                          {filePreview.trade_lic_image ? (
                            filePreview.trade_lic_image === 'pdf' ? (
                              <div className="py-4">
                                <span style={{ fontSize: '3rem' }}>📄</span>
                                <div className="fw-bold text-dark mt-2">Trade_License.pdf</div>
                                <span className="badge bg-primary mt-2">PDF Document Ready</span>
                              </div>
                            ) : (
                              <div>
                                <img src={filePreview.trade_lic_image} alt="License Preview" className="doc-preview" />
                                <div className="text-muted small mt-1">Click to change file</div>
                              </div>
                            )
                          ) : (
                            <div className="py-4">
                              <span style={{ fontSize: '3rem', color: '#94a3b8' }}>📁</span>
                              <h5 className="fw-bold mt-3 text-secondary">Click to upload Trade License</h5>
                              <p className="text-muted small mb-0">Images (JPEG, PNG) or PDF (Max 5MB)</p>
                            </div>
                          )}
                        </div>
                        {errors.trade_lic_image && (
                          <div className="error-message">
                            <span>❌</span> {errors.trade_lic_image}
                          </div>
                        )}
                      </div>

                      {/* GST Certificate Image */}
                      <div className="col-md-6 col-12">
                        <label className="senior-label">
                          GST Certificate Photo/PDF <span className="text-muted ms-2">(Optional)</span>
                        </label>
                        <span className="senior-help">Upload copy of your GST registration</span>

                        <div
                          className="doc-card"
                          onClick={() => document.getElementById('gst_image_input').click()}
                        >
                          <input
                            id="gst_image_input"
                            type="file"
                            accept="image/*,application/pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileChange(e, 'gst_image')}
                          />

                          {filePreview.gst_image ? (
                            filePreview.gst_image === 'pdf' ? (
                              <div className="py-4">
                                <span style={{ fontSize: '3rem' }}>📄</span>
                                <div className="fw-bold text-dark mt-2">GST_Certificate.pdf</div>
                                <span className="badge bg-primary mt-2">PDF Document Ready</span>
                              </div>
                            ) : (
                              <div>
                                <img src={filePreview.gst_image} alt="GST Preview" className="doc-preview" />
                                <div className="text-muted small mt-1">Click to change file</div>
                              </div>
                            )
                          ) : (
                            <div className="py-4">
                              <span style={{ fontSize: '3rem', color: '#94a3b8' }}>📁</span>
                              <h5 className="fw-bold mt-3 text-secondary">Click to upload GST copy</h5>
                              <p className="text-muted small mb-0">Images (JPEG, PNG) or PDF (Max 5MB)</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* --- SECTION 7: SUBMISSION & AGREEMENT --- */}
                <div className="card card-custom shadow-sm mb-5 bg-light border">
                  <div className="card-body p-4 p-md-5 text-center">
                    <div className="form-check d-inline-block text-start mb-4" style={{ maxWidth: '600px' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="agreement"
                        style={{ width: '24px', height: '24px', cursor: 'pointer', border: '2px solid #64748b' }}
                        onChange={(e) => setDisableSubmit(!e.target.checked)}
                      />
                      <label className="form-check-label text-dark fw-bold ms-3" htmlFor="agreement" style={{ fontSize: '1.05rem', cursor: 'pointer' }}>
                        I confirm that all information filled in this registration form is accurate, correct, and belongs to me or my business. I agree to the <a href="https://jivimart.com/additional/termsCondition" target="_blank" rel="noreferrer" className="text-primary text-decoration-underline">Terms & Conditions</a>.
                      </label>
                    </div>

                    <div className="d-grid gap-2 col-md-8 mx-auto mt-3">
                      <button
                        className="btn btn-primary btn-senior shadow"
                        disabled={disableSubmit}
                        onClick={handleSubmitAll}
                        style={{ padding: '20px 40px !important', fontSize: '1.4rem' }}
                      >
                        {disableSubmit ? '⚠️ Please Check Agreement Box to Enable Submit' : '🚀 Submit My Registration Form'}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageBackground: {
    backgroundColor: '#f1f5f9',
    minHeight: '100vh',
    fontFamily: '"Outfit", "Inter", system-ui, -apple-system, sans-serif',
    paddingBottom: '80px',
  },
  headerBanner: {
    backgroundColor: '#ffffff',
    padding: '15px 0',
    borderBottom: '2px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  mainTitle: {
    color: '#0f172a',
    fontSize: '2.5rem',
    fontWeight: '800',
    marginBottom: '10px',
  },
  subTitle: {
    color: '#475569',
    fontSize: '1.25rem',
    maxWidth: '700px',
    margin: '0 auto',
  },
  stepBadge: {
    backgroundColor: '#2563eb',
    color: '#ffffff',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.25rem',
    fontWeight: 'bold',
  },
  loadingOverlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(5px)',
  },
  loadingBox: {
    backgroundColor: '#ffffff',
    padding: '40px 50px',
    borderRadius: '24px',
    textAlign: 'center',
    boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
    maxWidth: '500px',
    width: '90%',
  },
};

export default RegisterAll;
